import Phaser from 'phaser';
import { GAME } from './config';
import { ResultScene } from './scenes/result-scene';
import { TitleScene } from './scenes/title-scene';
import { EndingScene } from './scenes/ending-scene';
import { TutorialScene } from './scenes/tutorial-scene';
import { TowerScene } from './scenes/tower-scene';
import { LoadingScene } from './scenes/loading-scene';
import { HorizontalBattleScene } from './scenes/horizontal-battle-scene';
import { HubScene } from './scenes/HubScene';
import { CampaignMapScene } from './scenes/CampaignMapScene';
import { MetaShopScene } from './scenes/meta-shop-scene';
import { GarageScene } from './scenes/GarageScene';

export class BootScene extends Phaser.Scene {
  constructor() { super('BootScene'); }
  create(): void {
    // BootScene is now the splash only; the mode router starts HorizontalBattleScene
    // directly via game.scene.start('HorizontalBattleScene') when entering battle mode.
    this.add.text(GAME.width / 2, GAME.height / 2, '永恆要塞', {
      fontSize: '48px', color: '#FFD54F', fontFamily: 'sans-serif',
    }).setOrigin(0.5);
  }
}

export function getGameConfig(): Phaser.Types.Core.GameConfig {
  return {
    type: Phaser.AUTO,
    width: GAME.width,
    height: GAME.height,
    backgroundColor: GAME.backgroundColor,
    parent: 'game-container',
    scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
    // P06 (iPad Multi-touch): Phaser ships with mouse + 1 touch pointer by
    // default. iPad gameplay needs simultaneous left-joystick + right-skill
    // taps (and we keep 1 spare for an accidental third finger). 3 active
    // pointers = pointer0 + pointer1 + pointer2 (id 1..3, mouse is id 0
    // separately). See docs/GAME_TECHNIQUES_WIKI.md §P06.
    //
    // Fallback note: on devices without multi-touch the extra pointer slots
    // just stay idle — desktop keyboard input continues to work unchanged.
    input: { activePointers: 3 },
    scene: [BootScene, LoadingScene, TitleScene, EndingScene, ResultScene, TutorialScene, TowerScene, HubScene, CampaignMapScene, MetaShopScene, GarageScene, HorizontalBattleScene],
  };
}

// crisp-text (boss 2026-06-16「字很糊」): the game renders at a fixed 800×600 and
// FIT-upscales to the screen, so on high-DPI phones every Phaser Text is blurry.
// Phaser Text honours a per-object `resolution` (renders the glyph texture at that
// scale), so we patch the GameObjectFactory.text default to devicePixelRatio —
// every `this.add.text()` becomes crisp with ZERO call-site changes. Only affects
// text-texture resolution (slightly bigger glyph textures); no layout/coords change.
let _crispTextPatched = false;
function applyCrispTextPatch(): void {
  if (_crispTextPatched) return;
  _crispTextPatched = true;
  try {
    const dpr = Math.min(3, Math.max(1, Math.round(window.devicePixelRatio || 1)));
    if (dpr <= 1) return; // 非高 DPI 無需處理
    const factory = Phaser.GameObjects.GameObjectFactory.prototype as unknown as {
      text: (x: number, y: number, text: string | string[], style?: Record<string, unknown>) => Phaser.GameObjects.Text;
    };
    const orig = factory.text;
    factory.text = function (x, y, text, style) {
      const s: Record<string, unknown> = { ...(style ?? {}) };
      if (s.resolution == null) s.resolution = dpr;
      return orig.call(this, x, y, text, s);
    };
  } catch { /* headless / no window — skip */ }
}

export function startGame(): Phaser.Game {
  applyCrispTextPatch();
  const g = new Phaser.Game(getGameConfig());
  // Expose for E2E FPS sampling: window.__phaserGame.loop.actualFps
  // Required by tests/e2e/auto-playthrough.spec.ts (captureFps helper).
  (window as unknown as { __phaserGame?: Phaser.Game }).__phaserGame = g;
  // 手機全屏 overscan 修正改由 main.ts 的 mountViewportFit 統一處理(它同時把
  // #game-container 尺寸綁到 visualViewport,再 scale.refresh,順序才正確)。
  return g;
}
